/**
 * Mock workflow data for the workflow frame showcase page.
 *
 * Each scenario demonstrates a different workflow pattern: sequential,
 * parallel fan-out/fan-in, conditional branching, mid-execution,
 * approval gates, and failure states.
 */

import type {
  FlowSpec,
  FlowStep,
  WorkflowRun,
} from "@/workshop/components/shapes/workflow-frame/types";

// ---------------------------------------------------------------------------
// Scenario 1: Simple sequential (all completed)
// ---------------------------------------------------------------------------

const sequentialSteps: FlowStep[] = [
  {
    id: "trigger",
    type: "trigger",
    title: "Manual trigger",
    icon: "Play",
    status: "completed",
    after: null,
    triggerConfig: { type: "manual" },
    durationMs: 0,
  },
  {
    id: "s1",
    type: "agent_task",
    title: "Analyze codebase",
    icon: "Bot",
    status: "completed",
    after: "trigger",
    prompt:
      "Scan the project for unused dependencies and outdated packages. List each with its current version and latest available version.",
    output:
      "Found 7 unused packages: chalk@4.1.2, lodash@4.17.21, moment@2.29.4, uuid@8.3.2, rimraf@3.0.2, glob@7.2.3, debug@4.3.4. All can be safely removed.",
    durationMs: 12400,
  },
  {
    id: "s2",
    type: "tool_call",
    title: "Remove packages",
    icon: "Terminal",
    status: "completed",
    after: "s1",
    toolName: "bash",
    toolParams: {
      command:
        "npm uninstall chalk lodash moment uuid rimraf glob debug && npm prune",
    },
    output: "Removed 7 packages. node_modules size reduced by 14.2 MB.",
    durationMs: 8200,
  },
  {
    id: "s3",
    type: "tool_call",
    title: "Run tests",
    icon: "Code",
    status: "completed",
    after: "s2",
    toolName: "bash",
    toolParams: { command: "npm test -- --coverage" },
    output: "All 142 tests passed. Coverage: 87.3% statements, 91.1% branches.",
    durationMs: 34600,
  },
];

export const MOCK_SEQUENTIAL: FlowSpec = {
  id: "wf-sequential",
  name: "Cleanup unused dependencies",
  trigger: { type: "manual" },
  steps: sequentialSteps,
};

// ---------------------------------------------------------------------------
// Scenario 2: Parallel branching with fan-out / fan-in (mid-execution)
// ---------------------------------------------------------------------------

const parallelSteps: FlowStep[] = [
  {
    id: "trigger",
    type: "trigger",
    title: "Weekly Monday 9 AM",
    icon: "Clock",
    status: "completed",
    after: null,
    triggerConfig: {
      type: "cron",
      schedule: "0 9 * * 1",
      timezone: "Europe/Berlin",
    },
    durationMs: 0,
  },
  {
    id: "s1",
    type: "agent_task",
    title: "Research competitors",
    icon: "Search",
    status: "completed",
    after: "trigger",
    prompt:
      "Research the top 5 competitors in the AI workspace market. Summarize their latest product updates, pricing changes, and notable features launched in the past week.",
    output:
      "Completed competitive analysis: Notion AI added canvas mode, Cursor launched multi-file editing, Replit added deployment pipelines, Cody expanded to 12 languages, Windsurf released v2 with memory.",
    durationMs: 45200,
  },
  {
    id: "s2a",
    type: "tool_call",
    title: "Email summary to team",
    icon: "Mail",
    status: "running",
    after: "s1",
    toolName: "gmail.sendEmail",
    toolParams: {
      to: "product-team@denker.ai",
      subject: "Weekly Competitive Intel — March 10, 2026",
      body: "{{s1.output}}",
    },
    liveStatus: "Composing email with formatted report...",
  },
  {
    id: "s2b",
    type: "tool_call",
    title: "Post to Slack",
    icon: "MessageSquare",
    status: "running",
    after: "s1",
    toolName: "slack.postMessage",
    toolParams: {
      channel: "#product-intel",
      text: "Weekly competitive update is ready. Key highlights: {{s1.output}}",
    },
    liveStatus: "Sending message to #product-intel...",
  },
  {
    id: "s3",
    type: "approval_gate",
    title: "Approval gate",
    icon: "check-circle",
    status: "pending",
    after: ["s2a", "s2b"],
  },
];

export const MOCK_PARALLEL: FlowSpec = {
  id: "wf-parallel",
  name: "Weekly competitive intelligence",
  trigger: { type: "cron", schedule: "0 9 * * 1", timezone: "Europe/Berlin" },
  steps: parallelSteps,
};

// ---------------------------------------------------------------------------
// Scenario 3: Conditional split
// ---------------------------------------------------------------------------

const conditionalSteps: FlowStep[] = [
  {
    id: "trigger",
    type: "trigger",
    title: "GitHub PR opened",
    icon: "git-branch",
    status: "completed",
    after: null,
    triggerConfig: { type: "event", eventDescription: "GitHub PR opened" },
    durationMs: 0,
  },
  {
    id: "s1",
    type: "agent_task",
    title: "Review PR code",
    icon: "Bot",
    status: "completed",
    after: "trigger",
    prompt:
      "Review the diff for code quality, security vulnerabilities, performance issues, and adherence to project coding standards. Report any issues found.",
    output:
      "Found 3 issues: 1) SQL injection risk in query builder (critical), 2) Missing null check on user.profile access (medium), 3) Console.log left in production code (low).",
    durationMs: 18700,
  },
  {
    id: "s2",
    type: "conditional",
    title: "Issues found?",
    icon: "git-branch",
    status: "completed",
    after: "s1",
    branches: [
      { label: "Yes", targetStepId: "s3a" },
      { label: "No", targetStepId: "s3b" },
    ],
  },
  {
    id: "s3a",
    type: "tool_call",
    title: "Post review comments",
    icon: "MessageSquare",
    status: "completed",
    after: "s2",
    toolName: "github.createReview",
    toolParams: {
      repo: "denker-ai/dolcetto",
      pr: "142",
      event: "REQUEST_CHANGES",
      body: "Found 3 issues that need to be addressed before merging.",
    },
    output: "Review submitted with 3 inline comments on PR #142.",
    durationMs: 3200,
  },
  {
    id: "s3b",
    type: "tool_call",
    title: "Approve PR",
    icon: "Check",
    status: "pending",
    after: "s2",
    toolName: "github.createReview",
    toolParams: {
      repo: "denker-ai/dolcetto",
      pr: "142",
      event: "APPROVE",
      body: "LGTM. No issues found.",
    },
  },
];

export const MOCK_CONDITIONAL: FlowSpec = {
  id: "wf-conditional",
  name: "Automated PR review",
  trigger: { type: "event", eventDescription: "GitHub PR opened" },
  steps: conditionalSteps,
};

// ---------------------------------------------------------------------------
// Scenario 4: Mid-execution (step 2 running with liveStatus)
// ---------------------------------------------------------------------------

const midrunSteps: FlowStep[] = [
  {
    id: "trigger",
    type: "trigger",
    title: "Manual trigger",
    icon: "Play",
    status: "completed",
    after: null,
    triggerConfig: { type: "manual" },
    durationMs: 0,
  },
  {
    id: "s1",
    type: "agent_task",
    title: "Scrape API docs",
    icon: "Globe",
    status: "completed",
    after: "trigger",
    prompt:
      "Crawl the Stripe API documentation and extract all endpoint definitions, request/response schemas, and authentication requirements.",
    output:
      "Scraped 247 endpoints across 18 resource categories. Extracted OpenAPI-compatible schemas for all request/response bodies.",
    durationMs: 62300,
  },
  {
    id: "s2",
    type: "agent_task",
    title: "Generate TypeScript types",
    icon: "Bot",
    status: "running",
    after: "s1",
    prompt:
      "Using the scraped API schemas, generate TypeScript type definitions for all Stripe API resources. Use strict types, no `any`.",
    liveStatus: "Generating types for Payment Intents (14 of 18 resources)...",
  },
  {
    id: "s3",
    type: "tool_call",
    title: "Write to file",
    icon: "File",
    status: "pending",
    after: "s2",
    toolName: "bash",
    toolParams: {
      command: "cat > src/types/stripe-api.d.ts",
    },
  },
];

export const MOCK_MIDRUN: FlowSpec = {
  id: "wf-midrun",
  name: "Generate Stripe API types",
  trigger: { type: "manual" },
  steps: midrunSteps,
};

// ---------------------------------------------------------------------------
// Scenario 5: Approval waiting
// ---------------------------------------------------------------------------

const approvalSteps: FlowStep[] = [
  {
    id: "trigger",
    type: "trigger",
    title: "Daily at 6 PM",
    icon: "Clock",
    status: "completed",
    after: null,
    triggerConfig: {
      type: "cron",
      schedule: "0 18 * * *",
      timezone: "Europe/Berlin",
    },
    durationMs: 0,
  },
  {
    id: "s1",
    type: "agent_task",
    title: "Generate daily report",
    icon: "Bot",
    status: "completed",
    after: "trigger",
    prompt:
      "Compile today's engineering metrics: PRs merged, deployments, incidents, and test coverage delta. Format as an executive summary.",
    output:
      "Daily Report — March 10, 2026: 8 PRs merged, 3 deployments (all green), 0 incidents, coverage +1.2% to 88.5%. Sprint velocity on track.",
    durationMs: 22100,
  },
  {
    id: "s2",
    type: "approval_gate",
    title: "Manager approval",
    icon: "check-circle",
    status: "waiting",
    after: "s1",
  },
  {
    id: "s3",
    type: "tool_call",
    title: "Send to stakeholders",
    icon: "Mail",
    status: "pending",
    after: "s2",
    toolName: "gmail.sendEmail",
    toolParams: {
      to: "leadership@denker.ai",
      subject: "Engineering Daily Report — {{date}}",
      body: "{{s1.output}}",
    },
  },
];

export const MOCK_APPROVAL: FlowSpec = {
  id: "wf-approval",
  name: "Daily engineering report",
  trigger: {
    type: "cron",
    schedule: "0 18 * * *",
    timezone: "Europe/Berlin",
  },
  steps: approvalSteps,
};

// ---------------------------------------------------------------------------
// Scenario 6: Failed step
// ---------------------------------------------------------------------------

const failedSteps: FlowStep[] = [
  {
    id: "trigger",
    type: "trigger",
    title: "Manual trigger",
    icon: "Play",
    status: "completed",
    after: null,
    triggerConfig: { type: "manual" },
    durationMs: 0,
  },
  {
    id: "s1",
    type: "tool_call",
    title: "Build project",
    icon: "Terminal",
    status: "completed",
    after: "trigger",
    toolName: "bash",
    toolParams: { command: "npm run build" },
    output: "Build completed successfully. Bundle size: 1.24 MB (gzip: 412 KB).",
    durationMs: 28400,
  },
  {
    id: "s2",
    type: "tool_call",
    title: "Run E2E tests",
    icon: "Code",
    status: "failed",
    after: "s1",
    toolName: "bash",
    toolParams: { command: "npx playwright test --project=chromium" },
    output: [
      "Running 48 tests using 4 workers",
      "",
      "  FAIL src/e2e/auth.spec.ts > login flow > should redirect after login",
      "    Error: Timed out waiting for selector '[data-testid=\"dashboard-header\"]'",
      "    Timeout: 30000ms",
      "    at AuthPage.waitForNavigation (src/e2e/pages/auth.ts:42:5)",
      "",
      "  FAIL src/e2e/auth.spec.ts > login flow > should show error on invalid credentials",
      "    Error: expect(received).toBeVisible()",
      "    Element '[data-testid=\"login-error-banner\"]' not found",
      "",
      "  46 passed, 2 failed",
      "  Total time: 2m 14s",
    ].join("\n"),
    durationMs: 134000,
  },
  {
    id: "s3",
    type: "tool_call",
    title: "Deploy to staging",
    icon: "Cloud",
    status: "pending",
    after: "s2",
    toolName: "bash",
    toolParams: { command: "npx vercel deploy --prebuilt" },
  },
];

export const MOCK_FAILED: FlowSpec = {
  id: "wf-failed",
  name: "Build, test & deploy",
  trigger: { type: "manual" },
  steps: failedSteps,
};

// ---------------------------------------------------------------------------
// Past workflow runs
// ---------------------------------------------------------------------------

export const MOCK_RUNS: WorkflowRun[] = [
  {
    id: "run-1",
    status: "completed",
    startedAt: "2026-03-10T09:00:00Z",
    completedAt: "2026-03-10T09:02:14Z",
    durationMs: 134000,
  },
  {
    id: "run-2",
    status: "failed",
    startedAt: "2026-03-09T09:00:00Z",
    completedAt: "2026-03-09T09:01:47Z",
    durationMs: 107000,
  },
  {
    id: "run-3",
    status: "completed",
    startedAt: "2026-03-08T09:00:00Z",
    completedAt: "2026-03-08T09:03:02Z",
    durationMs: 182000,
  },
  {
    id: "run-4",
    status: "completed",
    startedAt: "2026-03-07T09:00:00Z",
    completedAt: "2026-03-07T09:01:58Z",
    durationMs: 118000,
  },
];

// ---------------------------------------------------------------------------
// ExecutionTodo — mirrors production companion-store type
// ---------------------------------------------------------------------------

/** A todo item written by the agent's TodoWrite tool. */
export interface ExecutionTodo {
  /** Unique identifier. */
  id: string;
  /** Short task title. */
  title: string;
  /** Current status. */
  status: "pending" | "running" | "completed" | "failed";
}

export const MOCK_EXECUTION_TODOS: ExecutionTodo[] = [
  { id: "todo-1", title: "Refactor auth middleware", status: "completed" },
  { id: "todo-2", title: "Write unit tests for billing service", status: "running" },
  { id: "todo-3", title: "Fix N+1 query in project list", status: "pending" },
  { id: "todo-4", title: "Add dark mode to settings page", status: "failed" },
  { id: "todo-5", title: "Generate API documentation", status: "pending" },
  { id: "todo-6", title: "Update deployment scripts", status: "pending" },
];

// ---------------------------------------------------------------------------
// WorkflowStep — mirrors production companion-store type
// ---------------------------------------------------------------------------

/** A structured workflow step tracked during execution. */
export interface WorkflowStep {
  /** Step index (1-based ordering). */
  index: number;
  /** Human-readable step label. */
  label: string;
  /** Current status. */
  status: "pending" | "running" | "completed" | "waiting_approval" | "failed";
  /** Optional agent key running this step. */
  agentKey?: string;
}

export const MOCK_WORKFLOW_STEPS_RUNNING: WorkflowStep[] = [
  { index: 1, label: "Research competitors", status: "completed" },
  { index: 2, label: "Email summary to team", status: "running" },
  { index: 3, label: "Post to Slack", status: "pending" },
  { index: 4, label: "Approval gate", status: "pending" },
];

export const MOCK_WORKFLOW_STEPS_APPROVAL: WorkflowStep[] = [
  { index: 1, label: "Generate daily report", status: "completed" },
  { index: 2, label: "Manager approval", status: "waiting_approval" },
  { index: 3, label: "Send to stakeholders", status: "pending" },
];

export const MOCK_WORKFLOW_STEPS_COMPLETED: WorkflowStep[] = [
  { index: 1, label: "Manual trigger", status: "completed" },
  { index: 2, label: "Analyze codebase", status: "completed" },
  { index: 3, label: "Remove packages", status: "completed" },
  { index: 4, label: "Run tests", status: "completed" },
];

export const MOCK_WORKFLOW_STEPS_FAILED: WorkflowStep[] = [
  { index: 1, label: "Manual trigger", status: "completed" },
  { index: 2, label: "Build project", status: "completed" },
  { index: 3, label: "Run E2E tests", status: "failed" },
  { index: 4, label: "Deploy to staging", status: "pending" },
];

// ---------------------------------------------------------------------------
// All flow specs for easy iteration
// ---------------------------------------------------------------------------

export const ALL_MOCK_FLOWS: FlowSpec[] = [
  MOCK_SEQUENTIAL,
  MOCK_PARALLEL,
  MOCK_CONDITIONAL,
  MOCK_MIDRUN,
  MOCK_APPROVAL,
  MOCK_FAILED,
];
